import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth, CONSENT_PROMPT_SKIP_KEY } from '@/contexts/AuthContext';
import { ackKeyFor, type PendingTransition } from '@/components/StageTransitionSheet';

export type PromptType = 'consent' | 'username' | 'biometric' | 'stage_transition';

interface StartupPromptContextType {
  currentPrompt: PromptType | null;
  advanceQueue: () => void;
  backfilledUsername: string | null;
  pendingTransition: PendingTransition | null;
  currentUserId: string | null;
}

const StartupPromptContext = createContext<StartupPromptContextType | undefined>(undefined);

export const useStartupPrompt = (): StartupPromptContextType => {
  const ctx = useContext(StartupPromptContext);
  if (!ctx) throw new Error('useStartupPrompt must be used within StartupPromptProvider');
  return ctx;
};

interface Props {
  children: ReactNode;
}

export const StartupPromptProvider: React.FC<Props> = ({ children }) => {
  const { userProfile, session, refreshProfile } = useAuth();

  const [queue, setQueue] = useState<PromptType[]>([]);
  const [backfilledUsername, setBackfilledUsername] = useState<string | null>(null);
  const [pendingTransition, setPendingTransition] = useState<PendingTransition | null>(null);
  const initializedForUser = useRef<string | null>(null);

  const currentUserId = session?.user?.id ?? null;

  // Rebuild the queue for each distinct authenticated user.
  // Resets automatically on sign-out (session becomes null) and on re-login
  // with the same or different account in the same app process.
  useEffect(() => {
    const userId = session?.user?.id ?? null;

    if (!userId) {
      // Signed out — reset so next login gets a fresh queue
      initializedForUser.current = null;
      setQueue([]);
      setBackfilledUsername(null);
      setPendingTransition(null);
      return;
    }

    if (initializedForUser.current === userId) return;
    if (!userProfile || !session?.access_token) return;

    initializedForUser.current = userId;
    buildQueue(session.access_token);
  }, [userProfile, session?.user?.id]);

  const buildQueue = async (accessToken: string) => {
    // GATE: never surface startup prompts before the user has finished onboarding.
    // Google users authenticate BEFORE onboarding — their account (and session)
    // exists the moment they sign in — so without this gate the consent /
    // stage-transition sheets would pop over the onboarding funnel. Email users
    // only reach this code AFTER onboarding (their account is created at the final
    // step), so this is a no-op for them. Completion mirrors
    // profileService.getOnboardingCompletionStrict (interests ≥ 3, gender,
    // sponsorship preference) but reads the already-loaded profile to avoid an
    // extra network round trip.
    const onboardingComplete = !!(
      userProfile &&
      Array.isArray(userProfile.interests) && userProfile.interests.length >= 3 &&
      userProfile.sponsorship_preference !== null && userProfile.sponsorship_preference !== undefined &&
      userProfile.gender !== null && userProfile.gender !== undefined
    );
    if (!onboardingComplete) {
      console.log('⏭️ StartupPromptContext: onboarding incomplete, deferring startup prompts');
      // Clear the init guard so the queue is (re)built once the profile reflects
      // completed onboarding (refreshProfile runs after google/complete-profile).
      initializedForUser.current = null;
      return;
    }

    const pending: PromptType[] = [];

    // 1. Check consent status
    // Suppressed for fresh signups — set-password.tsx already records consent
    // to the current versions, so showing the sheet again right after signup
    // would be redundant. CONSENT_PROMPT_SKIP_KEY is a one-shot flag.
    try {
      const consentSkip = await AsyncStorage.getItem(CONSENT_PROMPT_SKIP_KEY);
      if (consentSkip === 'true') {
        await AsyncStorage.removeItem(CONSENT_PROMPT_SKIP_KEY);
        console.log('⏭️ StartupPromptContext: Consent prompt suppressed (fresh signup flag)');
      } else {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const res = await fetch('https://mobile.padrinho.app/api/consent/status', {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.needs_update) {
            pending.push('consent');
          }
        }
      }
    } catch {
      // non-blocking — if we can't check consent, skip it
    }

    // 2. Silent username backfill (no user-facing prompt)
    // Username is generated in the background on first login if missing. The
    // user discovers it later on the Profile page — no bottom sheet is shown.
    if (userProfile && !userProfile.username) {
      try {
        console.log('🔄 StartupPromptContext: Profile has no username, calling silent backfill...');
        const backfillRes = await fetch('https://mobile.padrinho.app/api/auth/profile/username/backfill', {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (backfillRes.ok) {
          const backfillData = await backfillRes.json();
          if (backfillData.success && backfillData.backfilled === true && backfillData.username) {
            console.log('✅ StartupPromptContext: Username silently backfilled');
            // Refresh the profile so the new username is available app-wide
            try { await refreshProfile(); } catch { /* non-blocking */ }
          }
        }
      } catch (backfillErr) {
        console.warn('⚠️ StartupPromptContext: Backfill failed (non-blocking):', backfillErr);
      }
    }

    // 3. Check pending stage transition (Recovery Journey RJ-7)
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const res = await fetch('https://mobile.padrinho.app/api/recovery-plan/today', {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        const data = await res.json();
        const pt: PendingTransition | null = data?.ok && data?.pendingTransition ? data.pendingTransition : null;
        if (pt && pt.toStageKey && pt.fromStageKey !== pt.toStageKey) {
          const userId = session?.user?.id;
          if (userId) {
            const ackedAt = await AsyncStorage.getItem(ackKeyFor(userId, pt));
            if (!ackedAt) {
              setPendingTransition(pt);
              pending.push('stage_transition');
            } else {
              console.log('⏭️ StartupPromptContext: Stage transition already acked');
            }
          }
        }
      }
    } catch {
      // non-blocking — if /today fails we just skip the celebration this cold start
    }

    // 4. Biometric enrolment prompt — REMOVED.
    // Biometric is now only surfaced in two places:
    //   (a) `app/login.tsx` auto-prompts on app open when saved creds exist
    //   (b) Manual toggle in `app/perfil.tsx` lets the user activate it
    // We no longer push an unsolicited bottom sheet here.

    console.log('📋 StartupPromptContext: Queue built:', pending);
    setQueue(pending);
  };

  const advanceQueue = () => {
    setQueue(prev => prev.slice(1));
  };

  const currentPrompt = queue[0] ?? null;

  return (
    <StartupPromptContext.Provider value={{ currentPrompt, advanceQueue, backfilledUsername, pendingTransition, currentUserId }}>
      {children}
    </StartupPromptContext.Provider>
  );
};
